import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Play } from "lucide-react";

function VideoCard({ video, index }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };

  const toggleSound = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.15 }}
      className="relative rounded-[1.75rem] overflow-hidden border border-emerald-500/20 bg-[#0D130F] group glow-emerald"
      data-testid={`video-card-${index}`}
    >
      <video ref={ref} src={video.url} poster={video.url.replace(".mp4", ".jpg")} autoPlay muted loop playsInline preload="metadata" className="w-full aspect-[9/16] max-h-[34rem] object-cover" onClick={togglePlay} />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-6 pointer-events-none">
        <span className="text-[10px] uppercase tracking-[0.25em] text-yellow-500 font-medium">{video.caption}</span>
        <p className="font-display text-lg font-bold text-white mt-1">{video.title}</p>
      </div>
      <div className="absolute top-4 right-4 flex gap-2">
        <button onClick={toggleSound} data-testid={`video-sound-btn-${index}`} className="w-10 h-10 rounded-full glass-panel border border-emerald-500/30 flex items-center justify-center text-emerald-400 hover:text-yellow-500 transition-colors duration-200" aria-label="Sonido">
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
      {!playing && (
        <button onClick={togglePlay} data-testid={`video-play-btn-${index}`} className="absolute inset-0 flex items-center justify-center bg-black/40">
          <span className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center glow-emerald">
            <Play className="w-7 h-7 text-black fill-black ml-1" />
          </span>
        </button>
      )}
    </motion.div>
  );
}

export function VideosSection({ videos }) {
  if (!videos || !videos.length) return null;
  return (
    <section id="videos" className="py-24 lg:py-32" data-testid="videos-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-12">
          <span className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-medium">Videos</span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-3">
            Míralo <span className="text-gold-gradient">en acción</span>
          </h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {videos.map((v, i) => (
            <VideoCard key={i} video={v} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
